<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            :no-close-on-esc="adding_media"
            :no-close-on-backdrop="adding_media"
        >
            <template #modal-title>
                Create Alternate Language Media from
                <strong>ID: {{ media_to_link.id }} Title: {{ media_to_link.title }}</strong>
            </template>

            <div class="row align-items-center">
                <div class="col-lg-12 order-lg-1 order-2">
                    <b-form-group
                        class="mt-3"
                        label="Select Alternate Media Language"
                    >
                        <language-multiselect
                            v-model="selected_language"
                            :options="alternateLanguageOptions"
                            :multiple="false"
                            @add="addLanguage"
                            @remove="removeLanguage"
                        />
                        <b-form-invalid-feedback :state="!selected_language ? false : null">
                            Language is required before being able to select alternate media
                        </b-form-invalid-feedback>
                    </b-form-group>
                    <b-form-group
                    >
                        <template v-slot:label>
                            <label>
                                New File (Optional)
                                <sup>
                                    <font-awesome-icon
                                        :icon="['fad', 'info-circle']"
                                        content="If no new file is provided, created media will share media url with original"
                                        class="text-secondary" v-tippy="{placement: 'top'}"
                                    />
                                </sup>
                            </label>
                        </template>
                        <b-form-file
                            v-model="new_file"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                        ></b-form-file>
                    </b-form-group>
                    <b-form-group
                    >
                        <template v-slot:label>
                            <label>
                                New Splash File (Optional)
                                <sup>
                                    <font-awesome-icon
                                        :icon="['fad', 'info-circle']"
                                        content="If no new splash file is provided, created media will share media splash url with original"
                                        class="text-secondary" v-tippy="{placement: 'top'}"
                                    />
                                </sup>
                            </label>
                        </template>
                        <b-form-file
                            v-model="new_splash_file"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                        ></b-form-file>
                    </b-form-group>
                </div>
            </div>

            <template #modal-footer>
                <div v-if="!adding_media">
                    <b-button
                        variant="primary"
                        @click="handleCreateAndLink()"
                        :disabled="validating()"
                    >
                        Create & Link Alternate Media
                    </b-button>
                </div>
                <div v-else>
                    <b-spinner small label="Linking..."></b-spinner>
                    Uploading & Linking New Media - This may take a few minutes
                </div>
                <b-button
                    variant="light"
                    @click="cancel"
                    :disabled="adding_media"
                >
                    Cancel
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import {mapState} from 'vuex'
import LanguageMultiselect from '@/components/LanguageMultiselect'
import languages from '@/common/languages'

export default {
    data: () => ({
        selected_language: null,
        languages: languages,
        adding_media: false,
        new_file: null,
        new_splash_file: null,
    }),
    props: [
        'media_to_link',
        'media_group',
        'value'
    ],
    components: {
        LanguageMultiselect
    },
    computed: {
        ...mapState({}),
        modal_id() {
            let modal_id = 'add-alternate-media-modal'
            return modal_id
        },
        alternateLanguageOptions() {
            return this.languages.filter(lang => lang.language !== this.media_to_link.language);
        },
    },
    watch: {
        'media_to_link.id': {
            handler(value) {
                // mark to remove once we have more than two languages
                this.setStartingSelectedLanguage();
            }
        },
    },
    methods: {
        validating() {
            return !(this.selected_language);
        },
        addLanguage(new_language) {
            this.selected_language = new_language;
        },
        removeLanguage() {
            this.selected_language = null;
            this.resetForm();
        },
        resetForm() {
            this.new_file = null;
            this.new_splash_file = null
        },
        cancel(event) {
            event.preventDefault()
            if (this.adding_media) {
                return
            }
            this.resetForm();
            this.hide()
        },
        hide() {
            this.resetForm();
            this.$emit('hideCreateAlternate')
        },
        hidden() {
            this.$emit('hideCreateAlternate')
        },
        findMediaByLanguage(mediaArray, language) {
            return mediaArray.find(media => media.language === language);
        },
        addTag(tag, media_id) {
            this.axios.post('/api/media/add_tag/' + media_id, {
                tag_id: tag.id
            })
        },
        async linkMediaToGroup(new_media_id) {
            try {
                const url = `/api/media/link_media_to_group/${this.media_to_link.id}/${new_media_id}/null`
                const response = await this.axios.post(url)
                this.adding_media = false
                if (!response) {
                    throw new Error('Failed to link media to group')
                }
                this.$bvToast.toast('Media was successfully linked', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                this.$emit('alternateAdded')
                this.hide()
            } catch (e) {
                this.$bvToast.toast('An error has occurred', {
                    title: 'Error',
                    variant: 'danger',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                this.adding_media = false
                this.resetForm()
            }
        },
        async handleCreateAndLink() {
            if (this.adding_media) {
                return
            }
            const linkedMedia = this.findMediaByLanguage(this.media_group, this.selected_language.language);
            if (linkedMedia) {
                let message = `
                    <h4>Selected Media already has ${this.selected_language.label} Media linked</h4>
                    <p>
                        <strong>
                            ${this.media_to_link.id} - ${this.media_to_link.title} (${this.media_to_link.language})
                        </strong>
                        is already linked to
                        <strong>${linkedMedia.id} - ${linkedMedia.title} (${linkedMedia.language})</strong>.
                        Creating and linking a new alternate language will replace this media with the newly created
                        media. Do you wish to continue?
                    </p>
                `
                const result = await this.$swal({
                    html: message,
                    showCancelButton: true,
                    confirmButtonText: 'Create & Link to new Media',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })
                if (result.value) {
                    await this.createAndLinkNewMedia()
                }
            } else {
                await this.createAndLinkNewMedia()
            }
        },
        async createAndLinkNewMedia() {
            if (this.adding_media) return
            this.adding_media = true

            let file = null
            let splash_file = null
            let form_data = new FormData()
            if (this.new_file) {
                file = this.new_file
            }
            if (this.new_splash_file) {
                splash_file = this.new_splash_file
            }

            let new_title = `${this.media_to_link.title} - ${this.selected_language.label} Version`

            let merlin_id_array = [];
            // format comma separated merlin_ids input into an array, only accept numbers
            if (this.media_to_link.merlin_ids) {
                merlin_id_array = this.media_to_link.merlin_ids
                    .split(',')
                    .map((id) => parseInt(id.trim(), 10))
                    .filter((id) => !isNaN(id))
            }
            // if no file is provided, we will have the new media share the url of original
            if (!file) {
                form_data.append('url', `${this.media_to_link.url}?lang=${this.selected_language.language}`)
            }
            if (!splash_file && this.media_to_link.splash_url) {
                form_data.append(
                    'splash_url',
                    `${this.media_to_link.splash_url}?lang=${this.selected_language.language}`
                )
            }
            form_data.append('type', this.media_to_link.type)
            form_data.append('file', file)
            form_data.append('splash_file', splash_file)
            form_data.append('title', new_title)
            form_data.append('language', this.selected_language.language)
            if (merlin_id_array.length > 0) {
                form_data.append('merlin_ids', merlin_id_array)
            }

            try {
                const response = await this.axios.post('/api/media/upload_new_alt_media/', form_data)
                if (!response) {
                    throw new Error('Failed to upload new alt media')
                }
                this.$bvToast.toast('Media was successfully uploaded', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                if (response.data.media_id) {
                    if (this.media_to_link.tags && this.media_to_link.tags.length > 0) {
                        for (let tag of this.media_to_link.tags) {
                            this.addTag(tag, response.data.media_id)
                        }
                    }
                }
                await this.linkMediaToGroup(response.data.media_id)
            } catch (e) {
                this.$bvToast.toast(
                    (e.response && e.response.data && e.response.data.message) || 'An error has occurred', {
                    title: 'Error',
                    variant: 'danger',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                this.adding_media = false
            }
        },
        setStartingSelectedLanguage() {
            // This function just sets the selected language based on the media_to_link language and is only needed while we have two languages (en & es)
            // once more languages are added we don't need to do this check, as you will want select beteween multiple languages in the select language dropdown
            if (this.media_to_link.language === 'es') {
                this.selected_language = {
                    language: 'en',
                    label: 'English',
                    flag_iso: 'us'
                }
            } else if (this.media_to_link.language === 'en') {
                this.selected_language = {
                    language: 'es',
                    label: 'Spanish',
                    flag_iso: 'es'
                }
            }
        },
    },
    mounted() {
        // mark to remove once we have more than two languages
        this.setStartingSelectedLanguage();
    },

}

</script>
